import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Link,
  Rating,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Star,
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import SEOHead from '../components/SEOHead';
import AdvisorInquiryForm from '../components/AdvisorInquiryForm';
import MarketplacePackageCard from '../components/MarketplacePackageCard';
import { useAuth } from '../contexts/AuthContext';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageCanvasSx,
  documentPageContainerSx,
  documentEyebrowSx,
  documentAuthFieldSx,
  documentAuthAlertSx,
} from '../constants/documentTheme';
import {
  formatBusinessTypeLabel,
  specialtyLabel,
  websiteHref,
} from '../constants/advisorDirectory';
import { partnerOxbloodBtnSx } from '../components/PartnerListChrome';
import { getPartnerPackagesListingPath } from '../utils/packageAgentFilter';

const ProfileDetailRow = ({ icon, label, children }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
    <Box sx={{ color: DOCUMENT.ink3, mt: 0.35, display: 'flex' }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: DOCUMENT.ink3,
          mb: 0.35,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  </Box>
);

const PublicAdvisorProfile = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [advisor, setAdvisor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [reviewStars, setReviewStars] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewExisting, setReviewExisting] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewSuccess, setReviewSuccess] = useState(false);

  const loadAdvisor = useCallback(async () => {
    try {
      setLoading(true);
      setNotFound(false);
      const response = await api.get(`/advisors/${slug}`);
      if (response.data?.success) {
        setAdvisor(response.data.advisor);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      console.error('Failed to load advisor:', err);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadAdvisor();
  }, [loadAdvisor]);

  useEffect(() => {
    if (!advisor) return;
    if (new URLSearchParams(window.location.search).get('ask') === '1') {
      setInquiryOpen(true);
    }
  }, [advisor]);

  useEffect(() => {
    if (!user || !slug) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get(`/advisors/${slug}/review`);
        if (cancelled) return;
        const review = response.data?.review;
        if (review?.stars) {
          setReviewStars(review.stars);
          setReviewComment(review.comment || '');
          setReviewExisting(true);
        }
      } catch {
        // A missing review or signed-out session leaves the form empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, slug]);

  const submitProfileReview = async () => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(`/advisors/${slug}`)}`);
      return;
    }
    if (!reviewStars) {
      setReviewError(t('tripRating.starsRequired'));
      return;
    }
    try {
      setReviewSaving(true);
      setReviewError('');
      setReviewSuccess(false);
      await api.put(`/advisors/${slug}/review`, {
        stars: reviewStars,
        comment: reviewComment,
      });
      setReviewExisting(true);
      setReviewSuccess(true);
      await loadAdvisor();
    } catch (err) {
      setReviewError(err.response?.data?.error || t('tripRating.loadFailed'));
    } finally {
      setReviewSaving(false);
    }
  };

  const openPackage = useCallback((pkg) => {
    navigate(`/packages/${pkg.slug}`);
  }, [navigate]);

  const pageTitle = advisor
    ? t('advisorDirectory.profileTitle', {
      name: advisor.contactName,
      company: advisor.companyName,
    })
    : t('advisorDirectory.title');

  const businessTypeLabel = advisor
    ? formatBusinessTypeLabel(t, advisor.businessType, advisor.businessTypeOther)
    : '';
  const websiteLink = advisor ? websiteHref(advisor.website) : null;
  const hasBioContent = Boolean(
    advisor?.description
    || businessTypeLabel
    || advisor?.locationLabel
    || advisor?.companyAddress
    || websiteLink
    || (advisor?.specialties || []).length > 0
  );

  return (
    <Box sx={documentPageCanvasSx} data-testid="advisor-profile-page">
      <SEOHead
        title={pageTitle}
        description={advisor?.description || t('advisorDirectory.seoDescription')}
      />
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: 4 }}>
        {loading ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
          </Box>
        ) : notFound ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 28, mb: 1 }}>
              {t('advisorDirectory.notFoundTitle')}
            </Typography>
            <Typography sx={{ color: DOCUMENT.ink3, mb: 2 }}>
              {t('advisorDirectory.notFoundBody')}
            </Typography>
            <Button onClick={() => navigate('/advisors')} sx={partnerOxbloodBtnSx}>
              {t('advisorDirectory.backToDirectory')}
            </Button>
          </Box>
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start', mb: 2 }}>
                {advisor.logo ? (
                  <Box
                    component="img"
                    src={advisor.logo}
                    alt=""
                    sx={{ width: 72, height: 72, objectFit: 'contain' }}
                  />
                ) : (
                  <Avatar
                    sx={{
                      width: 72,
                      height: 72,
                      bgcolor: DOCUMENT.oxblood,
                      color: DOCUMENT.ivory,
                      fontFamily: DOCUMENT.display,
                      fontSize: 28,
                    }}
                  >
                    {advisor.companyName?.charAt(0)}
                  </Avatar>
                )}
                <Box>
                  <Typography
                    component="h1"
                    sx={{
                      fontFamily: DOCUMENT.display,
                      fontSize: { xs: 30, md: 40 },
                      lineHeight: 1.1,
                    }}
                  >
                    {advisor.contactName}
                  </Typography>
                  <Typography sx={{ fontSize: 17, color: DOCUMENT.ink2, mt: 0.5 }}>
                    {advisor.companyName}
                  </Typography>
                  {advisor.locationLabel ? (
                    <Typography sx={{ fontSize: 14, color: DOCUMENT.ink3, mt: 1 }}>
                      {advisor.locationLabel}
                    </Typography>
                  ) : null}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2.5 }}>
                {advisor.isVerifiedPartner ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Star sx={{ fontSize: 18, color: DOCUMENT.accent }} />
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: DOCUMENT.ink }}>
                      {t('advisorDirectory.verifiedPartner')}
                    </Typography>
                  </Box>
                ) : null}
                {advisor.isFeatured ? (
                  <Typography sx={{ ...documentEyebrowSx, color: DOCUMENT.oxblood, fontSize: 10 }}>
                    {t('advisorDirectory.featuredAgency')}
                  </Typography>
                ) : null}
                {advisor.ratingSummary?.count > 0 ? (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                    data-testid="advisor-profile-rating-summary"
                  >
                    <Rating
                      value={Number(advisor.ratingSummary.average) || 0}
                      precision={0.1}
                      readOnly
                      size="small"
                    />
                    <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2 }}>
                      {t('tripRating.partnerAverage', {
                        average: advisor.ratingSummary.average,
                        count: advisor.ratingSummary.count,
                      })}
                    </Typography>
                  </Box>
                ) : null}
              </Box>

              <Box
                sx={{
                  bgcolor: DOCUMENT.surface,
                  border: `1px solid ${DOCUMENT.ruleHard}`,
                  p: { xs: 2, md: 2.5 },
                  mb: 3,
                }}
              >
                <Typography sx={{ ...documentEyebrowSx, mb: 2 }}>
                  {t('advisorDirectory.aboutTitle')}
                </Typography>

                {advisor.description ? (
                  <Typography
                    sx={{ fontSize: 15, lineHeight: 1.65, color: DOCUMENT.ink2, mb: 2.5, whiteSpace: 'pre-wrap' }}
                    data-testid="advisor-profile-description"
                  >
                    {advisor.description}
                  </Typography>
                ) : null}

                {hasBioContent ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {businessTypeLabel ? (
                      <ProfileDetailRow
                        icon={<BusinessIcon fontSize="small" />}
                        label={t('advisorDirectory.businessTypeLabel')}
                      >
                        <Typography sx={{ fontSize: 15, color: DOCUMENT.ink }}>
                          {businessTypeLabel}
                        </Typography>
                      </ProfileDetailRow>
                    ) : null}

                    {advisor.locationLabel ? (
                      <ProfileDetailRow
                        icon={<LocationIcon fontSize="small" />}
                        label={t('advisorDirectory.serviceAreaLabel')}
                      >
                        <Typography sx={{ fontSize: 15, color: DOCUMENT.ink }}>
                          {advisor.locationLabel}
                        </Typography>
                      </ProfileDetailRow>
                    ) : null}

                    {advisor.companyAddress ? (
                      <ProfileDetailRow
                        icon={<LocationIcon fontSize="small" />}
                        label={t('advisorDirectory.addressLabel')}
                      >
                        <Typography sx={{ fontSize: 15, color: DOCUMENT.ink, whiteSpace: 'pre-wrap' }}>
                          {advisor.companyAddress}
                        </Typography>
                      </ProfileDetailRow>
                    ) : null}

                    {websiteLink ? (
                      <ProfileDetailRow
                        icon={<LanguageIcon fontSize="small" />}
                        label={t('advisorDirectory.websiteLabel')}
                      >
                        <Link
                          href={websiteLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ fontSize: 15, color: DOCUMENT.oxblood }}
                        >
                          {advisor.website}
                        </Link>
                      </ProfileDetailRow>
                    ) : null}

                    {(advisor.specialties || []).length > 0 ? (
                      <ProfileDetailRow
                        icon={<PersonIcon fontSize="small" />}
                        label={t('advisorDirectory.specialtiesLabel')}
                      >
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                          {advisor.specialties.map((item) => (
                            <Chip
                              key={item}
                              label={specialtyLabel(t, item)}
                              size="small"
                              sx={{
                                borderRadius: 0,
                                bgcolor: DOCUMENT.canvas,
                                border: `1px solid ${DOCUMENT.rule}`,
                              }}
                            />
                          ))}
                        </Box>
                      </ProfileDetailRow>
                    ) : null}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 14.5, color: DOCUMENT.ink3, lineHeight: 1.55 }}>
                    {t('advisorDirectory.emptyBio')}
                  </Typography>
                )}
              </Box>

              {(advisor.packages || []).length > 0 ? (
                <Box sx={{ mb: 4 }} data-testid="advisor-profile-packages">
                  <Typography sx={{ ...documentEyebrowSx, mb: 0.75 }}>
                    {t('advisorDirectory.packagesTitle')}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, mb: 2 }}>
                    {t('advisorDirectory.packagesSubtitle', { company: advisor.companyName })}
                  </Typography>
                  <Grid container spacing={2.5}>
                    {advisor.packages.map((pkg) => (
                      <Grid item xs={12} sm={6} key={pkg.id}>
                        <MarketplacePackageCard
                          pkg={pkg}
                          testId={`advisor-profile-package-${pkg.id}`}
                          showOfferedBy={false}
                          onOpen={openPackage}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ) : null}

              {advisor.team?.length > 0 ? (
                <Box sx={{ mt: 1 }}>
                  <Typography sx={{ ...documentEyebrowSx, mb: 1.5 }}>
                    {t('advisorDirectory.teamTitle')}
                  </Typography>
                  <Grid container spacing={2}>
                    {advisor.team.map((member) => (
                      <Grid item xs={12} sm={6} key={member.name}>
                        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                          {member.avatar ? (
                            <Avatar src={member.avatar} alt="" sx={{ width: 40, height: 40 }} />
                          ) : (
                            <Avatar sx={{ width: 40, height: 40, bgcolor: DOCUMENT.oxblood }}>
                              {member.name?.charAt(0)}
                            </Avatar>
                          )}
                          <Box>
                            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                              {member.name}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3 }}>
                              {t(`partnerProfile.roles.${member.role || 'member'}`, member.role)}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ) : null}

              <Box sx={{ mt: 4 }} data-testid="advisor-profile-reviews">
                <Typography sx={{ ...documentEyebrowSx, mb: 1.5 }}>
                  {t('tripRating.publicHeading')}
                </Typography>
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    bgcolor: DOCUMENT.surface,
                    border: `1px solid ${DOCUMENT.ruleHard}`,
                  }}
                  data-testid="advisor-profile-review-form"
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: DOCUMENT.ink, mb: 1 }}>
                    {t('tripRating.leaveReview')}
                  </Typography>
                  {user ? (
                    <>
                      <Rating
                        name="advisor-profile-review"
                        value={reviewStars}
                        onChange={(_, value) => setReviewStars(value || 0)}
                        sx={{ mb: 2, color: DOCUMENT.oxblood }}
                      />
                      <TextField
                        label={t('tripRating.commentsLabel')}
                        placeholder={t('tripRating.commentsPlaceholder')}
                        value={reviewComment}
                        onChange={(event) => setReviewComment(event.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        inputProps={{ maxLength: 2000 }}
                        sx={{ ...documentAuthFieldSx, mt: 0, mb: 2 }}
                      />
                      {reviewError ? (
                        <Alert severity="error" sx={{ ...documentAuthAlertSx, mb: 2 }}>
                          {reviewError}
                        </Alert>
                      ) : null}
                      {reviewSuccess ? (
                        <Alert severity="success" sx={{ ...documentAuthAlertSx, mb: 2 }}>
                          {t('tripRating.success')}
                        </Alert>
                      ) : null}
                      <Button
                        onClick={submitProfileReview}
                        disabled={reviewSaving}
                        sx={partnerOxbloodBtnSx}
                        data-testid="advisor-profile-review-submit"
                      >
                        {reviewSaving
                          ? t('tripRating.submitting')
                          : reviewExisting
                            ? t('tripRating.update')
                            : t('tripRating.submit')}
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => navigate(`/login?redirect=${encodeURIComponent(`/advisors/${slug}`)}`)}
                      sx={partnerOxbloodBtnSx}
                      data-testid="advisor-profile-review-signin"
                    >
                      {t('tripRating.signInToReview')}
                    </Button>
                  )}
                </Box>
                {(advisor.ratings || []).map((item, index) => (
                    <Box
                      key={`${item.travelerName || 'traveler'}-${index}`}
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: DOCUMENT.surface,
                        border: `1px solid ${DOCUMENT.ruleHard}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Rating value={item.stars || 0} readOnly size="small" />
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                          {item.travelerName || t('tripRating.anonymousTraveler')}
                        </Typography>
                      </Box>
                      {item.comment ? (
                        <Typography sx={{ fontSize: 14.5, color: DOCUMENT.ink2, lineHeight: 1.55 }}>
                          {item.comment}
                        </Typography>
                      ) : null}
                    </Box>
                ))}
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  bgcolor: DOCUMENT.surface,
                  border: `1px solid ${DOCUMENT.ruleHard}`,
                  p: 3,
                }}
              >
                <Typography sx={{ ...documentEyebrowSx, mb: 1 }}>
                  {t('advisorDirectory.contactPanelTitle')}
                </Typography>
                <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2, mb: 2.5, lineHeight: 1.55 }}>
                  {t('advisorDirectory.contactPanelBody')}
                </Typography>
                <Button
                  fullWidth
                  onClick={() => setInquiryOpen(true)}
                  sx={partnerOxbloodBtnSx}
                  data-testid="advisor-profile-contact"
                >
                  {t('advisorDirectory.contactAdvisor')}
                </Button>
                {advisor.packageCount > 0 ? (
                  <Link
                    component={RouterLink}
                    to={getPartnerPackagesListingPath(advisor.id)}
                    data-testid="advisor-profile-packages-link"
                    sx={{
                      fontSize: 12.5,
                      color: DOCUMENT.ink3,
                      mt: 2,
                      display: 'block',
                      textDecoration: 'underline',
                      textDecorationColor: DOCUMENT.ruleHard,
                      '&:hover': { color: DOCUMENT.oxblood, textDecorationColor: DOCUMENT.oxblood },
                    }}
                  >
                    {t('advisorDirectory.packageCount', { count: advisor.packageCount })}
                  </Link>
                ) : null}
              </Box>
            </Grid>
          </Grid>
        )}
      </Container>

      {advisor ? (
        <AdvisorInquiryForm
          open={inquiryOpen}
          onClose={() => setInquiryOpen(false)}
          mode="general"
        partnerId={advisor.id}
        partnerName={advisor.companyName}
        teamMembers={advisor.team}
        fromDirectory
        />
      ) : null}
    </Box>
  );
};

export default PublicAdvisorProfile;
